import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  buildCareHandoff,
  buildObservationHandoffItems,
  buildTreatmentHandoffItems,
  expandRoutineSlots,
  expandSlots,
  type CareHandoffHistory,
  type CareHandoffItem,
  type Routine,
  type RoutineLog,
  type Treatment,
  type TreatmentLog,
} from '@petrhythm/core';
import { repository } from '@petrhythm/data';
import { Button, Card, EmptyState, ErrorState, SectionHeading, t } from '@petrhythm/ui';
import { saveProductFile, fileSaveMessage } from '../lib/file-delivery.js';
import { dateToEpoch, formatDateTime, todayKst } from '../lib/format.js';
import { track } from '../lib/platform.js';
import { usePlatform } from '../lib/platform-context.js';
import { useData } from './app.js';
import { readRecordHistory } from '../lib/record-history.js';

type Candidate = CareHandoffItem & { group: 'completed' | 'remaining' | 'observed' };

function statusOf(log?: TreatmentLog): CareHandoffItem['status'] {
  if (!log) return 'remaining';
  return log.status === 'given' ? 'done' : log.status;
}

function statusLabel(status: CareHandoffItem['status']): string {
  return {
    done: '완료',
    skipped: '건너뜀',
    refused: '거부',
    partial: '일부 완료',
    remaining: '미기록 · 확인 필요',
  }[status];
}

export function HandoffScreen() {
  const platform = usePlatform();
  const { petId } = useParams();
  const { pets } = useData();
  const pet = pets.find((item) => item.id === petId);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [history, setHistory] = useState<CareHandoffHistory[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectionContext = useRef('');
  const [note, setNote] = useState('');
  const [expiryHours, setExpiryHours] = useState<8 | 24 | 48>(24);
  const [createdAt, setCreatedAt] = useState(() => Date.now());
  const [message, setMessage] = useState('');
  const [sourceSignature, setSourceSignature] = useState('');
  const [busy, setBusy] = useState(false); const lock = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const date = todayKst();

  const load = async () => {
    if (!petId) return;
    setLoading(true);
    setError(false);
    try {
      const [treatments, treatmentLogs, routines, routineLogs, handoffs, recordHistory] = await Promise.all([
        repository.list<Treatment>('treatments'),
        repository.list<TreatmentLog>('treatmentLogs'),
        repository.list<Routine>('routines'),
        repository.list<RoutineLog>('routineLogs'),
        repository.list<CareHandoffHistory>('handoffs'),
        readRecordHistory(petId),
      ]);
      const observations: Candidate[] = buildObservationHandoffItems(recordHistory, date).map((item) => ({ ...item, group: 'observed' }));
      setSourceSignature(JSON.stringify([...([treatments, treatmentLogs, routines, routineLogs].map((rows) => rows.filter((row) => row.petId === petId))), observations]));
      const activeRoutines = routines.filter((item) => item.petId === petId && item.active);
      const petTreatmentLogs = treatmentLogs.filter((item) => item.petId === petId);
      const petRoutineLogs = routineLogs.filter((item) => item.petId === petId);
      const treatmentCandidates: Candidate[] = buildTreatmentHandoffItems({ petId, date, treatments, logs: treatmentLogs }).map((item) => ({ ...item, group: item.status === 'remaining' ? 'remaining' : 'completed' }));
      const routineCandidates: Candidate[] = activeRoutines.flatMap((routine) =>
        expandRoutineSlots(routine, 'Asia/Seoul', date, date).map((slot) => {
          const log = petRoutineLogs.find((item) => item.scheduledAt === slot.epoch && item.routineId === routine.id);
          return {
            id: log ? `routine-log:${log.id}` : `routine-slot:${routine.id}:${slot.epoch}`,
            kind: 'routine',
            label: routine.name,
            localTime: slot.localTime,
            status: log ? log.status === 'skipped' ? 'skipped' : 'done' : 'remaining',
            group: log ? 'completed' : 'remaining',
          };
        }),
      );
      const nextCandidates = [...treatmentCandidates, ...routineCandidates, ...observations]
        .sort((left, right) => left.localTime.localeCompare(right.localTime) || left.id.localeCompare(right.id));
      setCandidates(nextCandidates);
      if (selectionContext.current !== `${petId}:${date}`) { setSelected(new Set(nextCandidates.map((item) => item.id))); selectionContext.current = `${petId}:${date}`; }
      setHistory(handoffs.filter((item) => item.petId === petId).sort((left, right) => right.createdAt - left.createdAt));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [petId, date]);
  const chosen = useMemo(() => candidates.filter((item) => selected.has(item.id)), [candidates, selected]);
  const expiresAt = createdAt + expiryHours * 60 * 60 * 1_000;
  const { artifact, preparationError } = useMemo(() => {
    if (!pet || chosen.length === 0) return { artifact: undefined, preparationError: '' };
    try { return { artifact: buildCareHandoff({
      petName: pet.name,
      localDate: date,
      createdLabel: formatDateTime(createdAt),
      expiresLabel: formatDateTime(expiresAt),
      items: chosen,
      note: note.trim() || undefined,
    }), preparationError: '' }; }
    catch { return { artifact: undefined, preparationError: '고른 기록이 너무 길어요. 보낼 항목을 줄여 주세요. 원본은 그대로 남아 있어요.' }; }
  }, [chosen, createdAt, date, expiresAt, note, pet]);

  if (!pet) return <section className="screen container"><ErrorState messageKey="error.notFound" /></section>;
  if (loading) return <section className="screen container"><p>{t('common.loading')}</p></section>;
  if (error) return <section className="screen container"><ErrorState messageKey="error.loadFailed" onRetry={() => void load()} /></section>;

  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const remember = async (delivery: CareHandoffHistory['delivery'], deliveryResult: NonNullable<CareHandoffHistory['deliveryResult']>) => {
    if (!artifact) return;
    await repository.put<CareHandoffHistory>('handoffs', {
      petId: pet.id,
      localDate: date,
      expiresAt,
      selectedCount: artifact.selectedCount,
      completedCount: artifact.completedCount,
      remainingCount: artifact.remainingCount,
      delivery,
      deliveryResult,
    });
    track('handoff_created', { delivery, selectedCount: artifact.selectedCount, remainingCount: artifact.remainingCount });
    setCreatedAt(Date.now());
    await load();
  };
  const checkCurrent = async () => {
    if (expiresAt <= Date.now()) throw new Error('다시 확인할 시각이 지났어요. 원본을 다시 확인해 새 인계를 준비해 주세요.');
    const rows = await Promise.all([repository.list<Treatment>('treatments'), repository.list<TreatmentLog>('treatmentLogs'), repository.list<Routine>('routines'), repository.list<RoutineLog>('routineLogs')]);
    const observations = buildObservationHandoffItems(await readRecordHistory(pet.id), date).map((item) => ({ ...item, group: 'observed' }));
    if (JSON.stringify([...rows.map((items) => items.filter((row) => row.petId === pet.id)), observations]) !== sourceSignature) { await load(); throw new Error('오늘 기록이 바뀌었어요. 갱신한 원본과 선택을 확인한 뒤 다시 전달해 주세요.'); }
  };
  const run = async (task: () => Promise<void>) => { if (lock.current) return; lock.current = true; setBusy(true); setMessage(''); try { await task(); } catch (caught) { setMessage(caught instanceof Error && caught.name === 'AbortError' ? '공유를 취소했어요.' : caught instanceof Error ? caught.message : '완료하지 못했어요. 선택과 메모는 남아 있어요.'); } finally { lock.current = false; setBusy(false); } };
  const share = () => run(async () => {
    if (!artifact) return; await checkCurrent();
    const delivery = await platform.share.share({ title: `${pet.name} 돌봄 인계`, text: artifact.text });
    try { await remember(delivery === 'copied' ? 'copy' : 'share', delivery); setMessage(delivery === 'copied' ? '인계문을 복사했어요.' : '공유 화면에 인계문을 전달했어요. 실제 발송 여부를 확인해 주세요.'); }
    catch { setMessage('전달 응답은 확인했지만 인계 이력을 저장하지 못했어요. 중복 발송 전에 전달 화면을 확인해 주세요.'); }
  });
  const download = () => run(async () => {
    if (!artifact) return; await checkCurrent();
    const result = await saveProductFile(platform, `petrhythm-${date}-handoff.txt`, new Blob([artifact.text], { type: 'text/plain;charset=utf-8' }));
    if (result.status === 'saved' || result.status === 'requested') { try { await remember('download', result.status); } catch { setMessage(`${fileSaveMessage(result)} 인계 이력은 저장하지 못했어요.`); return; } }
    setMessage(fileSaveMessage(result));
  });

  return <section className="screen container handoff-screen">
    <Link className="back-link" to="/app">← 오늘</Link>
    <SectionHeading eyebrow={`${pet.name} · ${date}`} title={t('handoff.title')}>
      <p className="screen-lede">{t('handoff.intro')}</p>
    </SectionHeading>
    <Button disabled={busy} variant="ghost" onClick={() => { setCreatedAt(Date.now()); void load(); }}>원본 다시 확인 · 새 인계 준비</Button><Card className="handoff-privacy"><strong>{t('handoff.privacyTitle')}</strong><p>{t('handoff.privacyBody')}</p></Card>
    {candidates.length ? <div className="handoff-columns">
      {(['completed', 'remaining', 'observed'] as const).filter((group) => candidates.some((item) => item.group === group)).map((group) => <section key={group} aria-labelledby={`handoff-${group}`}>
        <div className="section-row"><h2 id={`handoff-${group}`}>{group === 'completed' ? '실제 결과가 있는 돌봄' : group === 'observed' ? '오늘 직접 남긴 관찰' : '결과를 확인할 일정'}</h2><span>{candidates.filter((item) => item.group === group).length}건</span></div>
        <div className="handoff-choice-list">{candidates.filter((item) => item.group === group).map((item) => <label className="handoff-choice" key={item.id}>
          <input disabled={busy} checked={selected.has(item.id)} onChange={() => toggle(item.id)} type="checkbox" />
          <span><strong>{item.label}</strong><small>{item.localTime} · {item.kind === 'observation' ? item.detail : statusLabel(item.status)}{item.actorLabel ? ` · ${item.actorLabel} 기록` : ''}</small></span>
        </label>)}</div>
      </section>)}
    </div> : <EmptyState title={t('handoff.empty')} description={t('handoff.emptyHint')} />}
    <Card className="handoff-compose">
      {preparationError ? <p role="alert">{preparationError}</p> : null}
      <label>{t('handoff.note')}<textarea disabled={busy} maxLength={500} onChange={(event) => setNote(event.target.value)} placeholder={t('handoff.notePlaceholder')} rows={4} value={note} /></label>
      <fieldset><legend>{t('handoff.expiry')}</legend><div className="choice-row">{([8, 24, 48] as const).map((hours) => <button disabled={busy} aria-pressed={expiryHours === hours} className={expiryHours === hours ? 'choice selected' : 'choice'} key={hours} onClick={() => setExpiryHours(hours)} type="button">{hours}시간</button>)}</div></fieldset>
      <p className="form-hint">{t('handoff.selectionHint')}</p>
    </Card>
    {artifact ? <Card className="handoff-preview"><div className="section-row"><h2>{t('handoff.preview')}</h2><span>{artifact.selectedCount}건</span></div><pre>{artifact.text}</pre><div className="button-row"><Button disabled={busy} onClick={() => void share()}>{t('handoff.share')}</Button><Button disabled={busy} variant="secondary" onClick={() => void download()}>{t('handoff.download')}</Button></div></Card> : <EmptyState title={t('handoff.selectEmpty')} description={t('handoff.selectionHint')} />}
    {message ? <p className="toast" role="status">{message}</p> : null}
    <section aria-labelledby="handoff-history-title"><div className="section-row"><h2 id="handoff-history-title">{t('handoff.history')}</h2></div>{history.length ? <div className="record-list">{history.slice(0, 10).map((item) => <Card className="handoff-history" key={item.id}><div><strong>{formatDateTime(item.createdAt)}</strong><span>결과 기록 {item.completedCount} · 미기록 {item.remainingCount} · 총 {item.selectedCount}건</span><small>{item.deliveryResult === 'saved' ? '기기 저장 응답 확인 · ' : item.deliveryResult === 'requested' ? '파일 다운로드 요청 · ' : item.deliveryResult === 'copied' ? '복사 · ' : '공유 요청 이력 · '}{item.expiresAt <= Date.now() ? '재확인이 필요한 인계' : `${formatDateTime(item.expiresAt)}까지 확인`}</small></div></Card>)}</div> : <p className="form-hint">{t('handoff.historyEmpty')}</p>}</section>
  </section>;
}
